import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { GAMES, GAME_KEYS, matchGrid } from '../engine';
import type { GameKey, Grid } from '../engine';
import { GridCard } from '../components/GridCard';
import { fetchDashboard, fetchHistory, fetchPayouts } from '../lib/api';
import type { DrawRecord, PayoutRow } from '../lib/api';
import { buildLedger, expectedWonCents } from '../lib/ledger';
import type { Ledger } from '../lib/ledger';
import {
  formatDateLong,
  formatEuros,
  formatInt,
  formatPercent,
  formatSignedPercent,
} from '../lib/format';
import { deleteGrid, exportGrids, importGrids, listGrids } from '../lib/storage';
import type { SavedGrid } from '../lib/storage';

/**
 * Saved grids, grouped by the draw they were saved for, checked against the result
 * as soon as it has been ingested. The checking is a plain comparison — there is
 * nothing to score.
 */
export function Grids(): ReactElement {
  const [grids, setGrids] = useState<SavedGrid[] | null>(null);
  const [results, setResults] = useState<Partial<Record<GameKey, DrawRecord | null>>>({});
  const [ledger, setLedger] = useState<Ledger | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const reload = useCallback((): void => {
    void listGrids()
      .then(setGrids)
      .catch(() => setGrids([]));
  }, []);

  useEffect(() => {
    reload();
    void fetchDashboard().then((d) => setResults(d.latest));
  }, [reload]);

  // The running total needs the result AND the amount paid at the rank each grid
  // reached, so it pulls history and the payout tables for the dates actually held.
  useEffect(() => {
    if (grids === null || grids.length === 0) {
      setLedger(null);
      return;
    }
    let alive = true;
    const games = GAME_KEYS.filter((k) => grids.some((g) => g.game === k));

    void Promise.all(
      games.map(async (game) => {
        const dates = [...new Set(grids.filter((g) => g.game === game).map((g) => g.drawDate))];
        const [history, payouts] = await Promise.all([
          fetchHistory(game),
          fetchPayouts(game, dates),
        ]);
        return { game, history: history.draws, payouts };
      }),
    ).then((parts) => {
      if (!alive) return;
      const resultsByGame: Partial<Record<GameKey, Map<string, DrawRecord>>> = {};
      const payoutsByGame: Partial<Record<GameKey, Record<string, PayoutRow[]>>> = {};
      for (const part of parts) {
        resultsByGame[part.game] = new Map(part.history.map((d) => [d.date, d]));
        payoutsByGame[part.game] = part.payouts;
      }
      setLedger(buildLedger(grids, { results: resultsByGame, payouts: payoutsByGame }));
    });

    return () => {
      alive = false;
    };
  }, [grids]);

  const groups = useMemo(() => {
    const map = new Map<string, SavedGrid[]>();
    for (const g of grids ?? []) {
      const key = `${g.drawDate}|${g.game}`;
      const bucket = map.get(key);
      if (bucket === undefined) map.set(key, [g]);
      else bucket.push(g);
    }
    return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [grids]);

  const doExport = useCallback((): void => {
    void exportGrids().then((json) => {
      // Safari on iOS blocks most download paths; the clipboard always works.
      void navigator.clipboard?.writeText(json);
      setMessage('Export copié dans le presse-papiers.');
    });
  }, []);

  const doImport = useCallback((): void => {
    void navigator.clipboard?.readText().then((text) =>
      importGrids(text)
        .then((n) => {
          setMessage(`${formatInt(n)} grille(s) importée(s).`);
          reload();
        })
        .catch(() => setMessage('Presse-papiers non reconnu.')),
    );
  }, [reload]);

  if (grids === null) return <div className="screen"><p className="dim">Chargement…</p></div>;

  const stake = grids.reduce((sum, g) => sum + GAMES[g.game].price, 0);

  return (
    <div className="screen">
      <h1>Grilles</h1>
      <p className="lede">
        {grids.length === 0
          ? 'Aucune grille enregistrée.'
          : `${formatInt(grids.length)} grille${grids.length > 1 ? 's' : ''} · mise cumulée ${formatEuros(stake)}`}
      </p>

      {message !== null && <div className="notice">{message}</div>}

      {ledger !== null && ledger.settled.length > 0 && (
        <div className="card">
          <div className="card-head">
            <span className="card-title">Bilan</span>
            <span className={`pill ${(ledger.returnRate ?? 0) >= 1 ? 'ok' : 'muted'}`}>
              {formatPercent(ledger.returnRate ?? 0, 0)} de retour
            </span>
          </div>
          <div className="card-sub">
            {formatInt(ledger.settled.length)} grille
            {ledger.settled.length > 1 ? 's' : ''} dont le tirage a eu lieu ·{' '}
            {formatInt(ledger.winningGrids)} gagnante
            {ledger.winningGrids > 1 ? 's' : ''}
          </div>

          <div className="table-wrap">
            <table>
              <tbody>
                <tr>
                  <td>Misé</td>
                  <td>{formatEuros(ledger.stakeCents / 100)}</td>
                </tr>
                <tr>
                  <td>Gagné</td>
                  <td>{formatEuros(ledger.wonCents / 100)}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Résultat</strong>
                  </td>
                  <td>
                    <strong>
                      {ledger.wonCents >= ledger.stakeCents ? '+' : '−'}
                      {formatEuros(Math.abs(ledger.wonCents - ledger.stakeCents) / 100)}
                    </strong>
                  </td>
                </tr>
                <tr>
                  <td className="dim">Attendu sur cette mise</td>
                  <td className="dim">{formatEuros(expectedWonCents(ledger) / 100)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {ledger.pending.length > 0 && (
            <p className="small dim" style={{ marginBottom: 0 }}>
              {formatInt(ledger.pending.length)} grille
              {ledger.pending.length > 1 ? 's' : ''} en attente de tirage (
              {formatEuros(ledger.pendingStakeCents / 100)}), hors bilan.
            </p>
          )}

          <p className="small faint" style={{ marginBottom: 0 }}>
            L’écart au montant attendu est du bruit tant que l’échantillon est petit :
            sur quelques dizaines de grilles il est dominé par le hasard, pas par la
            méthode. Le taux de retour converge vers{' '}
            {formatPercent(GAMES.loto.measuredRtp, 0)} au Loto et{' '}
            {formatPercent(GAMES.em.measuredRtp, 0)} à l’EuroMillions —{' '}
            {formatSignedPercent(GAMES.loto.measuredRtp - 1, 0)} et{' '}
            {formatSignedPercent(GAMES.em.measuredRtp - 1, 0)} par euro misé.
          </p>
        </div>
      )}

      {grids.length === 0 ? (
        <div className="empty">
          Passez par l’onglet Générateur, puis enregistrez le portefeuille produit.
        </div>
      ) : (
        groups.map(([key, bucket]) => {
          const first = bucket[0] as SavedGrid;
          const game = GAMES[first.game];
          const result = results[first.game] ?? null;
          const resultMatches = result !== null && result.date === first.drawDate;

          return (
            <section key={key}>
              <h2>
                {game.label} · {formatDateLong(first.drawDate)}
              </h2>

              {resultMatches ? (
                <div className="notice">
                  Résultat du tirage connu : {result.main.join(' - ')} ·{' '}
                  {game.bonusLabel} {result.bonus.join(' - ')}
                </div>
              ) : (
                <div className="notice">
                  Résultat non encore ingéré pour cette date.
                </div>
              )}

              {bucket.map((saved) => {
                const grid: Grid = { main: saved.main, bonus: saved.bonus };
                const m =
                  resultMatches && result !== null
                    ? matchGrid(grid, { main: result.main, bonus: result.bonus }, game)
                    : null;
                return (
                  <GridCard
                    key={saved.id}
                    game={game}
                    grid={grid}
                    badge={
                      m === null ? null : m.jackpot ? (
                        <span className="pill ok">rang 1</span>
                      ) : m.label === '' ? (
                        <span className="pill muted">aucun numéro</span>
                      ) : (
                        <span className="pill ok">{m.label}</span>
                      )
                    }
                    {...(m === null ? {} : { hitMain: m.matchedMain, hitBonus: m.matchedBonus })}
                    actions={
                      <button
                        type="button"
                        className="ghost danger"
                        onClick={() => {
                          void deleteGrid(saved.id).then(reload);
                        }}
                      >
                        Supprimer
                      </button>
                    }
                  />
                );
              })}
            </section>
          );
        })
      )}

      <hr />
      <h2>Sauvegarde</h2>
      <p className="small dim">
        Safari peut effacer les données d’un site non installé après sept jours sans
        visite. Installez l’application sur l’écran d’accueil, et exportez vos grilles :
        ce stockage est un cache, pas un coffre.
      </p>
      <div className="row">
        <button type="button" onClick={doExport}>
          Exporter
        </button>
        <button type="button" className="ghost" onClick={doImport}>
          Importer depuis le presse-papiers
        </button>
      </div>
    </div>
  );
}
